import { memo, useEffect, useRef, useState } from 'react';
import { ANALYTICS_EVENTS } from '@dunea/shared/analytics/events';
import { capture } from '~/lib/posthog';
import type { Suggestion, SuggestionKind } from './suggestion-chips';

/** Full class strings (not built at runtime) so UnoCSS can see every icon. */
const KIND_ICON: Record<SuggestionKind, string> = {
  page: 'i-ph:file-plus',
  section: 'i-ph:rows',
  feature: 'i-ph:lightning',
  polish: 'i-ph:magic-wand',
};

/** Seconds per full pass. Scaled by chip count so the speed reads the same at 4 or 5. */
const SECONDS_PER_CHIP = 4.5;

interface SuggestionChipsProps {
  items: Suggestion[];
  onPick: (suggestion: Suggestion) => void;
}

/**
 * "Ideas for next" — the chips above the composer after a build.
 *
 * They ride a marquee that drifts right to left, because 4–5 full-sentence
 * labels do not fit the composer's width and a grid of them pushed the input
 * down the screen. The track holds the list TWICE and translates by -50%, which
 * is what makes the loop seamless: at the halfway point the second copy sits
 * exactly where the first began, so the jump back to 0 is invisible.
 *
 * A moving target is hard to click, so the marquee stops on hover and on
 * keyboard focus. `prefers-reduced-motion` drops the animation entirely and
 * leaves a normal horizontal scroller.
 */
export const SuggestionChips = memo(({ items, onPick }: SuggestionChipsProps) => {
  const reported = useRef<string | null>(null);
  const [paused, setPaused] = useState(false);
  const signature = items.map((item) => item.label).join('|');

  useEffect(() => {
    if (items.length === 0 || reported.current === signature) {
      return;
    }

    reported.current = signature;
    capture(ANALYTICS_EVENTS.SUGGESTION_CHIPS_SHOWN, {
      count: items.length,
      kinds: items.map((item) => item.kind),
    });
  }, [signature, items]);

  if (items.length === 0) {
    return null;
  }

  const pick = (item: Suggestion, index: number) => {
    capture(ANALYTICS_EVENTS.SUGGESTION_CHIP_CLICKED, {
      kind: item.kind,
      position: index,
      label: item.label,
    });
    onPick(item);
  };

  const chip = (item: Suggestion, index: number, cloned: boolean) => (
    <button
      // The clones are decoration: a screen reader reads the list once.
      {...(cloned ? { tabIndex: -1, 'aria-hidden': true } : {})}
      key={`${cloned ? 'clone' : 'chip'}-${item.kind}-${item.label}`}
      type="button"
      title={item.prompt}
      aria-label={`Use suggestion: ${item.label}`}
      onClick={() => pick(item, index)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className="group flex shrink-0 items-center gap-2 min-h-9 max-w-[22rem] px-3 py-2 rounded-xl bg-raised border border-subtle font-geist text-left text-[12.5px] leading-snug touch:text-[13.5px] text-secondary cursor-pointer transition-colors hover:bg-sunken hover:text-primary hover:border-accent/45 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2"
    >
      <span className={`${KIND_ICON[item.kind]} shrink-0 text-[14px] text-accent`} aria-hidden="true" />
      <span className="truncate">{item.label}</span>
      {/* A pencil, not a send arrow: picking a chip fills the composer to edit. */}
      <span
        className="i-ph:pencil-simple shrink-0 text-[12px] text-accent opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
        aria-hidden="true"
      />
    </button>
  );

  return (
    <div className="flex flex-col gap-2 mb-2.5" data-testid="suggestion-chips">
      <div className="flex items-center gap-1.5 px-0.5 font-geist text-[10px] font-medium uppercase tracking-[0.14em] text-muted">
        <span className="i-ph:sparkle-fill text-[11px] text-accent" aria-hidden="true" />
        Ideas for next
      </div>

      {/* The mask fades both ends so chips enter and leave instead of being cut. */}
      <div
        className="group/marquee relative overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_2rem,black_calc(100%-2rem),transparent)]"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div
          className="flex w-max gap-2 motion-safe:animate-[suggestion-marquee_linear_infinite] motion-reduce:animate-none"
          style={{
            animationDuration: `${items.length * SECONDS_PER_CHIP}s`,
            animationPlayState: paused ? 'paused' : 'running',
          }}
        >
          {items.map((item, index) => chip(item, index, false))}
          {items.map((item, index) => chip(item, index, true))}
        </div>
      </div>
    </div>
  );
});
